import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

export interface SchoolSearchResult {
  id: string
  name: string
  city: string | null
}

export interface AffiliationRequest {
  id: string
  schoolId: string
  schoolName: string | null
  status: 'pending' | 'approved' | 'rejected'
  hourlyRate: number | null
  requestedAt: string
  decidedAt: string | null
}

interface RequestResult {
  ok: boolean
  code?: string
  request_id?: string
}

/**
 * An instructor's own contractor affiliation requests (migration 078) — the self-service
 * path onto a registered school's roster now that schools cannot invite contractors
 * directly (create_instructor_invitation()'s contractor_invite_not_allowed rejection).
 * searchSchools() calls search_registered_schools(), the one narrow RLS bypass that lets
 * an instructor discover a school they have no existing relationship with at all.
 */
export function useContractorAffiliationRequests() {
  const { user } = useAuth()
  const [requests, setRequests] = useState<AffiliationRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [requesting, setRequesting] = useState(false)

  const fetchRequests = useCallback(async () => {
    if (!user) return
    setLoading(true)
    try {
      // school_name is snapshotted onto the row at request time (migration 079), not
      // joined live — RLS correctly blocks reading the schools table for a school this
      // instructor isn't a member of yet, which is exactly the pending-request state.
      const { data } = await supabase
        .from('contractor_affiliation_requests')
        .select('id, school_id, status, hourly_rate, requested_at, decided_at, school_name')
        .eq('instructor_id', user.id)
        .order('requested_at', { ascending: false })

      setRequests((data ?? []).map((row: any) => ({
        id: row.id,
        schoolId: row.school_id,
        schoolName: row.school_name ?? null,
        status: row.status,
        hourlyRate: row.hourly_rate === null || row.hourly_rate === undefined ? null : Number(row.hourly_rate),
        requestedAt: row.requested_at,
        decidedAt: row.decided_at,
      })))
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => { fetchRequests() }, [fetchRequests])

  const searchSchools = useCallback(async (query: string): Promise<SchoolSearchResult[]> => {
    const { data, error } = await supabase.rpc('search_registered_schools', { p_query: query })
    if (error || !data) return []
    return (data as any[]).map(row => ({ id: row.id, name: row.name, city: row.city ?? null }))
  }, [])

  const requestAffiliation = useCallback(async (schoolId: string): Promise<RequestResult> => {
    if (requesting) return { ok: false, code: 'busy' }
    setRequesting(true)
    try {
      const { data, error: fnError } = await supabase.functions.invoke('request-contractor-affiliation', {
        body: { schoolId },
      })
      if (fnError) return { ok: false, code: 'internal_error' }
      if (data?.ok) await fetchRequests()
      return data as RequestResult
    } finally {
      setRequesting(false)
    }
  }, [requesting, fetchRequests])

  return { requests, loading, requesting, searchSchools, requestAffiliation, refresh: fetchRequests }
}
